import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const NotFound = () => {
  // Belt-and-suspenders: synchronously force-set the robots meta the moment
  // <NotFound> mounts. <RouteRobotsManager> already did this for unmatched
  // routes, but ServiceAreaPage→<NotFound> is on the *valid* `/:slug` pattern,
  // so the manager couldn't infer "404" from the URL alone. Lock the tag here
  // with data-fwd-locked="1" so the manager won't tear it back to "index".
  useEffect(() => {
    const setNoindex = () => {
      document.head.querySelectorAll('meta[name="robots"]').forEach((el) => el.remove());
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex, nofollow');
      meta.dataset.fwdLocked = '1';
      document.head.appendChild(meta);
    };
    setNoindex();
    return () => {
      document.head.querySelectorAll('meta[name="robots"][data-fwd-locked="1"]').forEach((el) => el.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Helmet>
        <title>Page Not Found | Fort Worth Dental</title>
        <meta name="description" content="The page you're looking for doesn't exist. Visit Fort Worth Dental for expert dental care in Fort Worth, TX." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="max-w-lg text-center">
        <div className="text-8xl font-bold text-teal-600 mb-4">404</div>
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Page Not Found</h1>
        <p className="text-gray-600 mb-8">
          Sorry, the page you're looking for doesn't exist or has been moved. 
          Let us help you find what you need.
        </p>
        
        <div className="space-y-4">
          <Link 
            to="/" 
            className="block w-full bg-teal-600 text-white py-3 px-6 rounded-lg hover:bg-teal-700 transition font-semibold"
          >
            Go to Homepage
          </Link>
          
          <div className="grid grid-cols-2 gap-4">
            <Link 
              to="/specials" 
              className="bg-white border border-teal-600 text-teal-600 py-2 px-4 rounded-lg hover:bg-teal-50 transition"
            >
              View Specials
            </Link>
            <Link 
              to="/contact-us" 
              className="bg-white border border-teal-600 text-teal-600 py-2 px-4 rounded-lg hover:bg-teal-50 transition"
            >
              Contact Us
            </Link>
          </div>
        </div>
        
        <div className="mt-12 p-6 bg-white rounded-lg shadow-sm">
          <h2 className="font-semibold text-gray-800 mb-3">Popular Pages</h2>
          <div className="flex flex-wrap justify-center gap-2">
            <Link to="/dental-implants" className="text-teal-600 hover:underline text-sm">Dental Implants</Link>
            <span className="text-gray-300">•</span>
            <Link to="/invisalign" className="text-teal-600 hover:underline text-sm">Invisalign</Link>
            <span className="text-gray-300">•</span>
            <Link to="/cosmetic-dentistry" className="text-teal-600 hover:underline text-sm">Cosmetic Dentistry</Link>
            <span className="text-gray-300">•</span>
            <Link to="/emergency-dentist" className="text-teal-600 hover:underline text-sm">Emergency Dentist</Link>
          </div>
        </div>
        
        <p className="mt-8 text-gray-500 text-sm">
          Need help? Call us at <a href="tel:8179853337" className="text-teal-600 font-semibold">(817) 985-3337</a>
        </p>
      </div>
    </div>
  );
};

export default NotFound;
